import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    TextField,
    Text,
    Stack,
    StackItem,
    ITextFieldStyles,
} from '@fluentui/react';
import ButtonPrimaryComponent from '../../components/button-primary/button-primary.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import LoaderComponent from '../../components/loader/loader.component';
import SuccessMessageComponent from '../../components/success-message/success-message.component';
import queryString from "query-string";
import { Buffer } from "buffer";

import ItemCliente from '../../models/ItemCliente';
import useAuthStatus from '../../hooks/auth/AuthHook';

const textFieldStyles : ITextFieldStyles = {
    root: {},
    fieldGroup: {
        borderColor: 'var(--color-primario)',
        borderRadius: 5,
        selectors: {
            "&:hover, &:focus, &:active": {
                borderColor: 'var(--color-primario)',
                borderRadius: 5,
            },
        },
    },
    prefix: {},
    suffix: {},
    field: {},
    icon: {},
    description: {},
    wrapper: {},
    errorMessage: {},
    subComponentStyles: {
        label: {},
    },
    revealButton: {},
    revealSpan: {},
    revealIcon: {},
};

interface IRecoverPasswordConfirmPageProps {};

interface IRecoverConfirmPageParams {
    ItemCliente: ItemCliente;
    Token: string;
};

const RecoverPasswordConfirmPage = (props: IRecoverPasswordConfirmPageProps) => {
    const auth = useAuthStatus();

    const [params, setParams] = useState<IRecoverConfirmPageParams>();
    const [error, setError] = useState<string>('');
    const [changePasswordError, setChangePasswordError] = useState<string>('');
    const [customer, setCustomer] = useState<ItemCliente>(new ItemCliente());
    const [loading, setLoading] = useState<boolean>(false);
    const [step, setStep] = useState<number>(1);
    const [success, setSuccess] = useState<string>('');

    const navigate = useNavigate();

    useEffect(() => {
        let queryParams : queryString.ParsedQuery<string> = queryString.parse(window.location.search);
        setParams(JSON.parse(Buffer.from(queryParams.ItemRecuperarConfirmar?.toString() || "", 'base64').toString('ascii')));
    }, []);

    useEffect(() => {
        if (params) {
            setLoading(true);
            auth.confirmRecover(customer, params.Token).then(() => {
                setStep(2);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    }, [params]);

    const onChangePassword = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer, 
            P1: newValue ? newValue : "",
        });
    };

    const onChangeConfirmPassword = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer, 
            P2: newValue ? newValue : "",
        });
    };

    const onKeyUp = (event: React.KeyboardEvent) : void => {
        if (event.key === "Enter") {
            onChangePasswordRecover();
        }
    };

    const onChangePasswordRecover = () : void => {
        if (params) {
            setChangePasswordError('');
            if (customer.P1 === "" || customer.P2 === "") {
                setChangePasswordError("Alguno de los campos introducidos no es válido.");
            }
            else if (customer.P1 !== customer.P2) {
                setChangePasswordError("Las contraseñas ingresadas no coinciden.");
            }
            else {
                setLoading(true);
                auth.changePasswordRecover(customer, params.Token).then((response: string) => {
                    setSuccess(response);
                    setStep(3);
                    setLoading(false);
                }, (error: string) => {
                    setChangePasswordError(error);
                    setLoading(false);
                });
            }
        }
    };

    return (
        <>
            {
                (step === 1) ? (
                    <Stack>
                        <StackItem className='mb-3'>
                            <Text 
                                className='fw-bold'
                                variant='large'>
                                Recuperar contraseña:
                            </Text>
                        </StackItem>
                        <StackItem className='mb-3'>
                            <ErrorMessageComponent message={error} />
                        </StackItem>
                        <StackItem className='mb-3'>
                            <ButtonPrimaryComponent 
                                className='text-uppercase'
                                text='Iniciar sesión'
                                onClick={() => navigate("/")} />
                        </StackItem>
                    </Stack>
                ) : (step === 2) ? (
                    <Stack>
                        <StackItem className='mb-3'>
                            <Text 
                                className='fw-bold'
                                variant='large'>
                                Recuperar contraseña:
                            </Text>
                        </StackItem>
                        <StackItem className='mb-3'>
                            <TextField
                                type="password"
                                autoComplete='off'
                                canRevealPassword={false}
                                onChange={onChangePassword}
                                defaultValue={customer.P1}
                                label="Nueva contraseña"
                                styles={textFieldStyles}
                                onKeyUp={onKeyUp} />
                        </StackItem>
                        <StackItem className='mb-3'>
                            <TextField
                                type="password"
                                autoComplete='off'
                                canRevealPassword={false}
                                onChange={onChangeConfirmPassword}
                                defaultValue={customer.P2}
                                label="Repetir nueva contraseña"
                                styles={textFieldStyles}
                                onKeyUp={onKeyUp} />
                        </StackItem>
                        <StackItem className='mb-3'>
                            <Stack 
                                horizontal={true}
                                className="d-flex align-items-center justify-content-between">
                                <StackItem>
                                    <ButtonPrimaryComponent 
                                        className='text-uppercase'
                                        text='Cambiar contraseña'
                                        onClick={onChangePasswordRecover} />
                                </StackItem>
                                <StackItem className="flex-fill text-center align-items-center">
                                    <Link 
                                        to="/"
                                        style={{
                                            color: 'var(--color-primario)',
                                            textDecoration: 'none',
                                        }}>
                                        Iniciar sesión
                                    </Link>
                                </StackItem>
                            </Stack>
                        </StackItem>
                        <StackItem className='mb-3'>
                            <ErrorMessageComponent message={changePasswordError} />
                        </StackItem>
                    </Stack>
                ) : (step === 3) ? (
                    <Stack>
                        <StackItem className='mb-3'>
                            <Text 
                                className='fw-bold'
                                variant='large'>
                                Recuperar contraseña:
                            </Text>
                        </StackItem>
                        <StackItem className='mb-3'>
                            <SuccessMessageComponent message={success} />
                        </StackItem>
                        <StackItem className='mb-3'>
                            <ButtonPrimaryComponent 
                                className='text-uppercase'
                                text='Iniciar sesión'
                                onClick={() => navigate("/")} />
                        </StackItem>
                    </Stack>
                ) : (
                    null
                )
            }
            <LoaderComponent loading={loading} />
        </>
    );
};

export default RecoverPasswordConfirmPage;
